import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';

const ContactSubmissions = () => {
  const { id: clientId } = useParams();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [pagination, setPagination] = useState({});
  const [statusCounts, setStatusCounts] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  const fetchSubmissions = async (page = 1, status = 'all') => {
    try {
      setLoading(true);
      console.log('Making API call to:', `/contact/submissions/${clientId}?page=${page}&limit=10&status=${status}`);
      console.log('Full URL:', axios.defaults.baseURL + `/contact/submissions/${clientId}?page=${page}&limit=10&status=${status}`);
      const response = await axios.get(
        `/contact/submissions/${clientId}?page=${page}&limit=10&status=${status}`
      );
      console.log('API Response:', response.data);
      setSubmissions(response.data.submissions);
      setPagination(response.data.pagination);
      setStatusCounts(response.data.statusCounts);
    } catch (error) {
      console.error('Error fetching contact submissions:', error);
      console.error('Error details:', error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  };

  const updateSubmissionStatus = async (submissionId, newStatus) => {
    try {
      await axios.patch(`/contact/submissions/${submissionId}/status`, {
        status: newStatus
      });
      // Refresh the list
      fetchSubmissions(currentPage, statusFilter);
    } catch (error) {
      console.error('Error updating submission status:', error);
    }
  };

  useEffect(() => {
    console.log('ContactSubmissions useEffect triggered');
    console.log('clientId:', clientId);
    console.log('axios.defaults.baseURL:', axios.defaults.baseURL);
    console.log('Authorization header:', axios.defaults.headers.common['Authorization']);
    fetchSubmissions(currentPage, statusFilter);
  }, [clientId, currentPage, statusFilter]);

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('lt-LT');
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': 'bg-blue-100 text-blue-800',
      'read': 'bg-yellow-100 text-yellow-800',
      'replied': 'bg-green-100 text-green-800',
      'closed': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusText = (status) => {
    const texts = {
      'new': 'Nauja',
      'read': 'Perskaityta',
      'replied': 'Atsakyta',
      'closed': 'Uždaryta'
    };
    return texts[status] || status;
  };

  console.log('Render state:', { loading, submissions: submissions.length, statusCounts });
  
  if (loading && submissions.length === 0) {
    console.log('Showing loading spinner');
    return <LoadingSpinner />;
  }

  console.log('Rendering main content with', submissions.length, 'submissions');
  console.log('Submissions data:', submissions);
  console.log('Status counts:', statusCounts);
  
  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Kontaktų formos pranešimai</h1>
        
        {/* Status Filter Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              {[
                { key: 'all', label: 'Visi', count: Object.values(statusCounts).reduce((a, b) => a + b, 0) },
                { key: 'new', label: 'Nauji', count: statusCounts.new || 0 },
                { key: 'read', label: 'Perskaityti', count: statusCounts.read || 0 },
                { key: 'replied', label: 'Atsakyti', count: statusCounts.replied || 0 },
                { key: 'closed', label: 'Uždaryti', count: statusCounts.closed || 0 }
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => handleStatusFilter(tab.key)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    statusFilter === tab.key
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label} ({tab.count})
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Submissions List */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          {submissions.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Nėra kontaktų formos pranešimų</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {submissions.map((submission) => (
                <div key={submission._id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <h3 className="text-lg font-medium text-gray-900 mr-3">
                          {submission.name}
                        </h3>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(submission.status)}`}>
                          {getStatusText(submission.status)}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div>
                          <p className="text-sm text-gray-500">El. paštas</p>
                          <p className="text-sm text-gray-900">{submission.email}</p>
                        </div>
                        {submission.phone && (
                          <div>
                            <p className="text-sm text-gray-500">Telefonas</p>
                            <p className="text-sm text-gray-900">{submission.phone}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-sm text-gray-500">Pateikta</p>
                          <p className="text-sm text-gray-900">{formatDate(submission.submittedAt)}</p>
                        </div>
                      </div>
                      
                      <div className="mb-4">
                        <p className="text-sm text-gray-500 mb-1">Žinutė</p>
                        <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded">
                          {submission.message}
                        </p>
                      </div>

                      {submission.sourceUrl && submission.sourceUrl !== 'Unknown' && (
                        <div className="mb-4">
                          <p className="text-sm text-gray-500">Šaltinis</p>
                          <p className="text-xs text-blue-600 truncate">{submission.sourceUrl}</p>
                        </div>
                      )}
                    </div>
                    
                    <div className="ml-6 flex flex-col space-y-2">
                      <select
                        value={submission.status}
                        onChange={(e) => updateSubmissionStatus(submission._id, e.target.value)}
                        className="text-sm border border-gray-300 rounded px-3 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="new">Nauja</option>
                        <option value="read">Perskaityta</option>
                        <option value="replied">Atsakyta</option>
                        <option value="closed">Uždaryta</option>
                      </select>
                      
                      <a
                        href={`mailto:${submission.email}?subject=Re: Jūsų užklausa&body=Sveiki ${submission.name},%0D%0A%0D%0ADėkojame už jūsų užklausą...`}
                        className="text-sm bg-indigo-600 text-white px-3 py-1 rounded hover:bg-indigo-700 text-center"
                      >
                        Atsakyti
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="mt-6 flex justify-center">
            <nav className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Ankstesnis
              </button>
              
              <span className="px-3 py-2 text-sm font-medium text-gray-700">
                {currentPage} iš {pagination.pages}
              </span>
              
              <button
                onClick={() => setCurrentPage(Math.min(pagination.pages, currentPage + 1))}
                disabled={currentPage === pagination.pages}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Kitas
              </button>
            </nav>
          </div>
        )}
    </div>
  );
};

export default ContactSubmissions;